import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faSpinner } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import Checkbox from '@/components/ui/Checkbox';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import { toCurrencyCentValue } from '@/utils/helpers';
import type { RecommendationFavorite } from '@/models';

interface EditFavoriteModalProps {
  isOpen: boolean;
  onClose: () => void;
  favorite: RecommendationFavorite;
  onFavoriteUpdated: (favorite: RecommendationFavorite) => void;
}

export default function EditFavoriteModal({
  isOpen,
  onClose,
  favorite,
  onFavoriteUpdated,
}: EditFavoriteModalProps) {
  const [entryPrice, setEntryPrice] = useState('');
  const [exitPrice, setExitPrice] = useState('');
  const [amount, setAmount] = useState('');
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setEntryPrice(favorite.entryPrice !== null ? (favorite.entryPrice / 100).toFixed(2) : '');
    setExitPrice(favorite.exitPrice !== null ? (favorite.exitPrice / 100).toFixed(2) : '');
    setAmount(favorite.amount !== null ? String(favorite.amount) : '');
    setActive(favorite.active ?? false);
  }, [favorite]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updated = await APIRoutes.updateFavorite(favorite.id, {
        entryPrice: entryPrice.trim() !== '' ? toCurrencyCentValue(parseFloat(entryPrice)) : null,
        exitPrice: exitPrice.trim() !== '' ? toCurrencyCentValue(parseFloat(exitPrice)) : null,
        amount: amount.trim() !== '' ? parseFloat(amount) : null,
        active: active,
      });
      onFavoriteUpdated(updated);
      showSuccessToast('Favorit gespeichert');
      onClose();
    } catch (error) {
      handleAPIError(error, 'Deine Änderungen am Favoriten konnten nicht gespeichert werden. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Favorit bearbeiten"
      icon={<FontAwesomeIcon icon={faHeart} className="w-5 h-5 text-red-500" />}
      maxWidth="md"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Hinterlege hier deine persönlichen Trade-Daten für diesen Favoriten. Alle Felder sind optional.
        </p>

        {/* Entry Price */}
        <div>
          <label htmlFor="favEntryPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Einstiegspreis
          </label>
          <input
            id="favEntryPrice"
            type="number"
            value={entryPrice}
            onChange={(e) => setEntryPrice(e.target.value)}
            step="0.01"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="z.B. 150.00"
            disabled={loading}
          />
        </div>

        {/* Exit Price */}
        <div>
          <label htmlFor="favExitPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Ausstiegspreis
          </label>
          <input
            id="favExitPrice"
            type="number"
            value={exitPrice}
            onChange={(e) => setExitPrice(e.target.value)}
            step="0.01"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="z.B. 175.00"
            disabled={loading}
          />
        </div>

        {/* Amount */}
        <div>
          <label htmlFor="favAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Menge
          </label>
          <input
            id="favAmount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            step="any"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="z.B. 10"
            disabled={loading}
          />
        </div>

        <Checkbox
          checked={active}
          onChange={setActive}
          disabled={loading}
          colorTheme="red"
          label="Aktiv"
          description="Markiere diesen Trade als aktuell aktiv."
        />

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Speichern...
              </>
            ) : (
              'Speichern'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
